import { useState } from 'react'
import { ChevronRight, Star, HandCoins } from 'lucide-react'
import { cn, formatKickoff, formatOdds, liveScoreLabel, probabilityDisplay, resolvePickBadgeStatus } from '@/lib/utils'
import { ConfidenceBadge } from './ConfidenceBadge'
import { RiskBadge } from './RiskBadge'
import { StatusBadge } from './StatusBadge'
import { TeamCrest } from './TeamCrest'
import { RunBadge } from './RunBadge'
import { RegisterBetModal } from './RegisterBetModal'
import { PlayedBadge } from './PlayedBadge'
import { useViewSettings } from '@/hooks/useViewSettings'
import type { PickLeg } from '@/types/miniApp'

function formatSelectionLabel(market: string, selection: string, home?: string | null, away?: string | null): string {
  const m = (market || '').toLowerCase()
  const s = (selection || '').toLowerCase()
  if (m === '1x2') {
    if (s === '1') return `Gana ${home || 'Local'}`
    if (s === '2') return `Gana ${away || 'Visitante'}`
    if (s === 'x' || s === 'draw') return 'Empate'
  }
  if (m === 'btts') {
    if (s === 'yes' || s === 'si') return 'Ambos marcan'
    if (s === 'no') return 'Ambos NO marcan'
  }
  if (m.includes('over') || m.includes('under') || m.startsWith('total')) {
    const parts = m.split('_')
    const line = parts[1] || ''
    if (s === 'over') return `Más de ${line} goles`
    if (s === 'under') return `Menos de ${line} goles`
  }
  return `${market}:${selection}`
}

export function TopPickCard({
  pick,
  rank,
  variant = 'ai',
  onSelect,
}: {
  pick: PickLeg
  rank: number
  variant?: 'ai' | 'safest'
  onSelect?: () => void
}) {
  const { isFavorite, toggleFavorite } = useViewSettings()
  const [showPlayModal, setShowPlayModal] = useState(false)
  const isTopOne = rank === 1
  const recordIdStr = String(pick.decision_record_id)
  const isFav = isFavorite(recordIdStr)
  const isSafest = variant === 'safest'

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleFavorite(recordIdStr)
  }

  const homeName = pick.home_team ?? 'Local'
  const awayName = pick.away_team ?? 'Visitante'
  // Sprint "Product Experience V2" (05/09/2026, Sección 3 del encargo): "probabilidad" es
  // un dato realmente útil que debía quedar visible en la card, no solo dentro de "Ver
  // análisis" -- honesto ante fallback/desconocido, MISMA función que ya usa el detalle.
  const probDisplay = probabilityDisplay(pick.model_probability, pick.probability_is_calibrated)
  // P1.10 (25/09/2026, Secciones 1/2 del encargo): MISMA lectura canónica de estado en toda
  // la app -- 'en_juego' cuando el fixture está live, nunca "Pendiente" fijo mientras el
  // partido se juega.
  const badgeStatus = resolvePickBadgeStatus(pick.fixture_status, pick.settled_status)
  const isLive = pick.fixture_status === 'live'
  const liveScore = isLive ? liveScoreLabel(pick.home_score, pick.away_score) : null

  return (
    <div
      onClick={onSelect}
      className={cn(
        'relative overflow-hidden cursor-pointer rounded-2xl border transition-all duration-300 active-scale hover-float p-4 space-y-4 shadow-lg',
        isSafest
          ? 'border-emerald-500/50 bg-gradient-to-b from-emerald-950/40 via-panel to-panel shadow-[0_4px_25px_rgba(16,185,129,0.15)]'
          : isTopOne
          ? 'border-brand/50 bg-gradient-to-b from-brand-soft/30 to-panel shadow-[0_4px_25px_rgba(167,237,32,0.12)]'
          : 'border-border bg-panel hover:border-text-dim/40'
      )}
    >
      {/* Glow ambiental superior */}
      {isSafest ? (
        <div
          className="absolute -top-12 -left-12 h-28 w-28 rounded-full opacity-35 blur-2xl pointer-events-none"
          style={{ background: '#10b981' }}
        />
      ) : isTopOne ? (
        <div
          className="absolute -top-12 -left-12 h-28 w-28 rounded-full opacity-35 blur-2xl pointer-events-none"
          style={{ background: 'rgb(var(--jd-brand))' }}
        />
      ) : null}

      {/* Cabecera de la Tarjeta */}
      <div className="flex items-center justify-between z-10 relative">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'flex items-center justify-center h-6 w-6 rounded-full text-xs font-black shadow-inner font-mono',
              isSafest
                ? 'bg-emerald-500 text-black'
                : isTopOne
                ? 'bg-brand text-black'
                : 'bg-panel-elevated text-brand border border-border'
            )}
          >
            #{rank}
          </span>
          <span className="text-[10px] font-black uppercase tracking-wider text-text-muted">
            {isSafest ? 'Top Seguro' : `Top Pick ${isTopOne ? 'Principal' : ''}`}
          </span>
          {pick.run_type && <RunBadge runType={pick.run_type} compact />}
        </div>

        <div className="flex items-center gap-2">
          {badgeStatus && <StatusBadge status={badgeStatus} />}
          <span className="text-[10px] font-bold text-text-dim font-mono">
            {formatKickoff(pick.kickoff_utc)}
          </span>
          <button
            onClick={handleFavoriteClick}
            className="p-1.5 rounded-xl bg-panel-elevated hover:bg-border text-text-dim hover:text-brand transition"
          >
            <Star
              className={cn('h-4 w-4', isFav ? 'fill-brand text-brand' : 'text-text-dim')}
            />
          </button>
        </div>
      </div>

      {/* Equipos con Escudos Grandes (48px) y VS Central */}
      <div className="bg-panel-elevated/40 p-3 rounded-2xl border border-border/40 space-y-2">
        <span className="text-[9px] font-extrabold uppercase tracking-wider text-text-dim block text-center">
          {pick.league ?? 'Competición Oficial'}
        </span>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col items-center gap-1.5 w-24 text-center">
            <TeamCrest src={pick.home_team_logo} alt={homeName} size={48} className="shadow-md" />
            <span className="text-xs font-black text-text line-clamp-1">{homeName}</span>
          </div>

          <span className="text-sm font-mono font-black text-text-dim">VS</span>

          <div className="flex flex-col items-center gap-1.5 w-24 text-center">
            <TeamCrest src={pick.away_team_logo} alt={awayName} size={48} className="shadow-md" />
            <span className="text-xs font-black text-text line-clamp-1">{awayName}</span>
          </div>
        </div>
        {/* P1.10 (Sección 2 del encargo): marcador EN VIVO real -- solo si el dato existe,
            nunca un minuto inventado (no hay campo real de minuto todavía). */}
        {isLive && liveScore && (
          <p className="text-center text-sm font-mono font-black text-brand">{liveScore}</p>
        )}
      </div>

      {/* Mercado y Selección Destacada */}
      <div className="bg-emerald-950/30 border border-emerald-500/30 p-3 rounded-xl flex items-center justify-between">
        <div className="space-y-0.5">
          <p className="text-[9px] font-black uppercase tracking-wider text-emerald-400">SELECCIÓN OFICIAL</p>
          <p className="text-sm font-black text-text">
            {pick.selection_label ?? formatSelectionLabel(pick.market ?? '', pick.selection ?? '', pick.home_team, pick.away_team)}
          </p>
          <p className="text-[10px] text-text-dim font-bold">{pick.market_label ?? pick.market}</p>
        </div>

        <div className="text-right">
          <span className="text-[9px] font-bold text-text-dim uppercase block">CUOTA</span>
          <span className="text-xl font-black text-emerald-400 font-mono">@{formatOdds(pick.odds)}</span>
          {/* Sección E (25/09/2026, "Odds Truth"): BUG REAL corregido -- `pick.odds` es
              `DecisionRecord.odds`, congelada en el momento de la decisión junto con
              `reference_bookmaker`/`odds_captured_at` (misma foto). `pick.bookmaker` es una
              lectura EN VIVO e independiente (`MatchOdds` más reciente), que puede ser otro
              operador -- mostrarla justo debajo del número, en el mismo bloque visual, atribuía
              esa cuota congelada al operador equivocado (canario real: "@1.92" + "BETANO",
              cuando el 1.92 pertenece a bet365). Ahora el nombre pegado al número es siempre el
              bookmaker AL QUE PERTENECE ese número (`reference_bookmaker`, con fallback a
              `bookmaker` solo si no hay referencia registrada); el bookmaker en vivo, si difiere,
              se muestra aparte y explícitamente rotulado como "actual", nunca junto al precio. */}
          {(pick.reference_bookmaker ?? pick.bookmaker) && (
            <p className="text-[8px] font-bold text-text-dim uppercase mt-0.5">
              {pick.reference_bookmaker ?? pick.bookmaker}
            </p>
          )}
          {pick.odds_captured_at && (
            <p className="text-[7px] text-text-dim mt-0.5">
              Capturada: {formatKickoff(pick.odds_captured_at, { withWeekday: false })}
            </p>
          )}
          {pick.bookmaker && pick.reference_bookmaker && pick.bookmaker !== pick.reference_bookmaker && (
            <p className="text-[7px] text-text-dim">
              Actualmente en {pick.bookmaker}
            </p>
          )}
        </div>
      </div>

      {/* P0.32 (26/08/2026, rediseño Mini App -- Sección 3 del encargo): resultado real del
          mercado cuando el pick ya liquidó -- nunca solo el badge ganada/perdida/void. */}
      {pick.settled_status && (pick.score_display || pick.market_result_display || pick.stat_result_display) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {pick.score_display && pick.score_display !== 'Pendiente' && (
            <span className="text-[9px] font-black uppercase text-brand bg-brand-soft/20 border border-brand/20 px-2 py-0.5 rounded-lg inline-block">
              Resultado final: {pick.score_display}
            </span>
          )}
          {pick.market_result_display && (
            <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
              {pick.market_result_display}
            </span>
          )}
          {pick.stat_result_display && (
            <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
              {pick.stat_result_display}
            </span>
          )}
        </div>
      )}

      {/* Footer de Tarjeta con Métricas */}
      <div className="flex items-center justify-between pt-2 border-t border-border/40 text-xs">
        <div className="flex items-center gap-3">
          {/* Sprint "Corrección Puntual -- Labs + Probabilidad Cliente" (10/09/2026, Sección 3
              del encargo): BUG REAL corregido -- solo se mostraba con `kind === 'calibrated'`,
              así que un `fallback` real (valor no-nulo, efectivamente usado por JA) se
              ocultaba en silencio, igual que un `unknown` genuino. `probabilityDisplay` ya
              expone `label` también en `fallback` (ver `lib/utils.ts`) -- se muestra siempre
              que exista un número real. */}
          {probDisplay.kind !== 'unknown' && (
            <div className="flex flex-col items-start gap-1">
              <span className="text-[9px] uppercase font-bold tracking-wider text-text-dim">Probabilidad</span>
              <span className="figure text-xs font-black text-text">{probDisplay.label}</span>
            </div>
          )}
          <ConfidenceBadge score={pick.confidence_score} />
          {pick.risk_level && <RiskBadge level={pick.risk_level} />}
        </div>

        <button className="text-brand font-black flex items-center gap-1 text-2xs hover:underline">
          <span>Ver análisis</span>
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Fase R.11 (Bloque G, 17/08/2026): "LA JUGUÉ" -- desde cualquier recomendación
          oficial se puede registrar la apuesta real en Mi Banca Personal.
          Fase R.12 (Bloque B, 17/08/2026): bug real de producción -- `pick.settled_status`
          vale 'pendiente' (nunca null) para CUALQUIER pick vivo con PaperTrade asociado
          (creado automáticamente para toda recomendación 'recomendar', ver
          `run_create_paper_trades`) -- `!pick.settled_status` ocultaba el botón en
          prácticamente todos los picks reales, solo se veía si estaba realmente liquidado
          Y con settled_status ausente (caso raro). Se compara contra los estados TERMINALES
          reales, nunca contra la mera presencia del campo. */}
      {/* Fase R.13 (17/08/2026): "LA JUGUÉ" registra una apuesta que el usuario YA hizo por
          su cuenta -- nunca "coloca" una nueva. Bloquearlo por `is_bettable`/`settled_status`
          (partido en vivo/finalizado) le impedía registrar una apuesta real hecha horas
          antes del kickoff -- bug real reportado tras probarlo en producción. La única
          condición real es que todavía no lo haya registrado (`my_bet`). */}
      {pick.my_bet ? (
        <PlayedBadge bet={pick.my_bet} />
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowPlayModal(true)
          }}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-panel-elevated border border-border/60 text-text-dim hover:text-brand hover:border-brand/40 text-[10px] font-black uppercase tracking-wider transition"
        >
          <HandCoins className="h-3.5 w-3.5" />
          La jugué
        </button>
      )}

      {showPlayModal && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          category={isSafest ? 'safest' : 'top_ia'}
          matchLabel={`${homeName} vs ${awayName}`}
          selectionLabel={pick.selection_label ?? formatSelectionLabel(pick.market ?? '', pick.selection ?? '', pick.home_team, pick.away_team)}
          referenceOdds={pick.odds ?? 0}
          decisionRecordId={pick.decision_record_id}
        />
      )}
    </div>
  )
}
